import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { TIMEZONE_HEADER, isHeaderSkippedByPolicy } from '../api/interceptors/header-injection.context';
import {
  canonicalizeTimeZone,
  detectBrowserTimeZone,
  TIMEZONE_CANONICALIZER,
} from '../utils/formatters/timezone-canonicalizer';
import { FHIR_API_BASE_URL } from './api-client.tokens';

export { TIMEZONE_HEADER };

function isFhirRequest(url: string, baseUrl: string | null | undefined): boolean {
  if (!baseUrl) {
    return false;
  }

  const normalizedBase = baseUrl.replace(/\/+$/, '');
  const pathPart = url.replace(/^https?:\/\/[^/]+/, '') || url;

  return (
    pathPart === normalizedBase ||
    pathPart.startsWith(`${normalizedBase}/`) ||
    url === normalizedBase ||
    url.startsWith(`${normalizedBase}/`)
  );
}

/**
 * TimezoneInterceptor
 *
 * Centralized functional HTTP interceptor that automatically attaches the client's canonical IANA TimeZone ID (e.g.
 * 'America/New_York', 'Asia/Kolkata') via the `X-Timezone` HTTP header to all outgoing API requests (excluding standard
 * FHIR endpoints and requests configured with skip header policy).
 *
 * Legacy aliases (e.g., 'Asia/Calcutta', 'US/Eastern') are dynamically normalized to canonical IANA zones using the
 * injectable TIMEZONE_CANONICALIZER.
 */
export const timezoneInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  // If the header is already explicitly set, pass through unchanged
  if (req.headers.has(TIMEZONE_HEADER)) {
    return next(req);
  }

  // Check if header is skipped via HttpContext policy
  if (isHeaderSkippedByPolicy(req.context, TIMEZONE_HEADER)) {
    return next(req);
  }

  // FHIR endpoints are standard HL7 FHIR APIs and must NOT receive non-standard application headers
  const fhirBaseUrl = inject(FHIR_API_BASE_URL, { optional: true });
  if (isFhirRequest(req.url, fhirBaseUrl)) {
    return next(req);
  }

  const canonicalize = inject(TIMEZONE_CANONICALIZER, { optional: true }) ?? canonicalizeTimeZone;
  const timeZone = canonicalize(detectBrowserTimeZone());
  const clonedReq = req.clone({
    setHeaders: {
      [TIMEZONE_HEADER]: timeZone,
    },
  });

  return next(clonedReq);
};
